// Données mock pour itérer sur le design.
// 4 projets représentant les états possibles : complet / en cours avec 4 candidats /
// en cours au démarrage / incomplet (pas de RC). Couvre tous les empty states.

import type {
  Project,
  Candidate,
  Criterion,
  UploadedFile,
  Evaluation,
  Citation,
} from './types';

// Référence de date : aujourd'hui (2026-04-22 selon le contexte).
// Helpers internes pour construire des ISO strings relatives.
const DAY = 86_400_000;
const HOUR = 3_600_000;
const now = new Date('2026-04-22T14:30:00Z').getTime();
const ago = (ms: number) => new Date(now - ms).toISOString();

// ─────────────────────────────────────────────────────────────────────
// Projet 1 — Rénovation énergétique Mairie de Gennevilliers (phare, 4 candidats)
// ─────────────────────────────────────────────────────────────────────

const GENNEVILLIERS_FILES: UploadedFile[] = [
  {
    id: 'f-g-rc-1',
    name: 'Règlement de consultation.pdf',
    sizeBytes: 2_300_000,
    pages: 18,
    uploadedAt: ago(12 * DAY),
    source: 'regulation',
  },
  {
    id: 'f-g-rc-2',
    name: 'CCTP - Annexe technique.pdf',
    sizeBytes: 5_100_000,
    pages: 42,
    uploadedAt: ago(12 * DAY),
    source: 'regulation',
  },
  {
    id: 'f-g-c1-1',
    name: 'Mémoire technique Bouygues Énergies.pdf',
    sizeBytes: 14_200_000,
    pages: 87,
    uploadedAt: ago(9 * DAY),
    source: 'candidate',
    candidateId: 'c-g-1',
  },
  {
    id: 'f-g-c2-1',
    name: 'Mémoire technique Eiffage Construction.pdf',
    sizeBytes: 18_500_000,
    pages: 124,
    uploadedAt: ago(8 * DAY),
    source: 'candidate',
    candidateId: 'c-g-2',
  },
  {
    id: 'f-g-c2-2',
    name: 'Note de calcul thermique.pdf',
    sizeBytes: 3_200_000,
    pages: 22,
    uploadedAt: ago(8 * DAY),
    source: 'candidate',
    candidateId: 'c-g-2',
  },
  {
    id: 'f-g-c3-1',
    name: 'Mémoire technique Engie Solutions.pdf',
    sizeBytes: 9_800_000,
    pages: 64,
    uploadedAt: ago(7 * DAY),
    source: 'candidate',
    candidateId: 'c-g-3',
  },
  {
    id: 'f-g-c4-1',
    name: 'Mémoire technique Vinci Énergies.pdf',
    sizeBytes: 11_600_000,
    pages: 73,
    uploadedAt: ago(6 * DAY),
    source: 'candidate',
    candidateId: 'c-g-4',
  },
];

const GENNEVILLIERS_CANDIDATES: Candidate[] = [
  { id: 'c-g-1', name: 'Bouygues Énergies & Services', fileIds: ['f-g-c1-1'], totalPages: 87, status: 'verified' },
  { id: 'c-g-2', name: 'Eiffage Construction', fileIds: ['f-g-c2-1', 'f-g-c2-2'], totalPages: 146, status: 'verified' },
  { id: 'c-g-3', name: 'Engie Solutions', fileIds: ['f-g-c3-1'], totalPages: 64, status: 'in_progress' },
  { id: 'c-g-4', name: 'Vinci Énergies', fileIds: ['f-g-c4-1'], totalPages: 73, status: 'todo' },
];

// 6 critères top-level, certains avec sous-critères → ~19 critères au total.
const GENNEVILLIERS_CRITERIA: Criterion[] = [
  // 1. Valeur technique (60 pts)
  { id: 'k-g-1',   parentId: null,    code: '1',     title: 'Valeur technique',                                  weight: 60, type: 'non_financial', scope: 'global', active: true, order: 0 },
  { id: 'k-g-1.1', parentId: 'k-g-1', code: '1.1',   title: 'Performance énergétique de la solution proposée',   weight: 20, type: 'non_financial', scope: 'global', active: true, order: 0 },
  { id: 'k-g-1.2', parentId: 'k-g-1', code: '1.2',   title: 'Méthodologie d\'exécution',                         weight: 15, type: 'non_financial', scope: 'global', active: true, order: 1 },
  { id: 'k-g-1.3', parentId: 'k-g-1', code: '1.3',   title: 'Moyens humains et matériels dédiés',                weight: 10, type: 'non_financial', scope: 'global', active: true, order: 2 },
  { id: 'k-g-1.4', parentId: 'k-g-1', code: '1.4',   title: 'Qualité des matériaux et équipements',              weight: 15, type: 'non_financial', scope: 'global', active: true, order: 3 },
  // 2. Prix (30 pts)
  { id: 'k-g-2',   parentId: null,    code: '2',     title: 'Prix des prestations',                              weight: 30, type: 'financial',     scope: 'global', active: true, order: 1 },
  // 3. Délais (5 pts)
  { id: 'k-g-3',   parentId: null,    code: '3',     title: 'Délais d\'exécution',                               weight: 5,  type: 'non_financial', scope: 'global', active: true, order: 2 },
  { id: 'k-g-3.1', parentId: 'k-g-3', code: '3.1',   title: 'Respect du planning prévisionnel',                  weight: 3,  type: 'non_financial', scope: 'global', active: true, order: 0 },
  { id: 'k-g-3.2', parentId: 'k-g-3', code: '3.2',   title: 'Gestion des aléas et phases critiques',             weight: 2,  type: 'non_financial', scope: 'global', active: true, order: 1 },
  // 4. RSE (5 pts)
  { id: 'k-g-4',   parentId: null,    code: '4',     title: 'Démarche environnementale et sociale',              weight: 5,  type: 'non_financial', scope: 'global', active: true, order: 3 },
  { id: 'k-g-4.1', parentId: 'k-g-4', code: '4.1',   title: 'Gestion des déchets de chantier',                   weight: 2,  type: 'non_financial', scope: 'global', active: true, order: 0 },
  { id: 'k-g-4.2', parentId: 'k-g-4', code: '4.2',   title: 'Insertion sociale et clauses d\'insertion',         weight: 3,  type: 'non_financial', scope: 'global', active: true, order: 1 },
  // 5. SAV & maintenance (désactivé, exemple d'un critère écarté)
  { id: 'k-g-5',   parentId: null,    code: '5',     title: 'Service après-vente et maintenance',                weight: 0,  type: 'non_financial', scope: 'global', active: false, order: 4 },
  { id: 'k-g-5.1', parentId: 'k-g-5', code: '5.1',   title: 'Disponibilité du SAV',                              weight: 0,  type: 'non_financial', scope: 'global', active: false, order: 0 },
  { id: 'k-g-5.2', parentId: 'k-g-5', code: '5.2',   title: 'Garantie étendue',                                  weight: 0,  type: 'non_financial', scope: 'global', active: false, order: 1 },
  // 6. Sécurité chantier (désactivé)
  { id: 'k-g-6',   parentId: null,    code: '6',     title: 'Sécurité et protection des usagers',                weight: 0,  type: 'non_financial', scope: 'global', active: false, order: 5 },
];

// Évaluations partielles : les 2 premiers candidats ont tous leurs critères leaf notés,
// le 3ème en cours, le 4ème vierge. 9 critères leaf actifs × 4 candidats = 36 évaluations attendues,
// dont 9+9+4 = 22 évaluées → progress ≈ 61%.
const GENNEVILLIERS_LEAF_ACTIVE_IDS = [
  'k-g-1.1', 'k-g-1.2', 'k-g-1.3', 'k-g-1.4',
  'k-g-2',
  'k-g-3.1', 'k-g-3.2',
  'k-g-4.1', 'k-g-4.2',
];

const makeEval = (
  candidateId: string,
  criterionId: string,
  aiScore: number,
  weight: number,
  status: 'conforme' | 'partial' | 'non_conforme',
  options: { userValidated?: boolean; userScore?: number } = {}
): Evaluation => {
  const base: Evaluation = {
    id: `e-${candidateId}-${criterionId}`,
    projectId: 'p-gennevilliers',
    candidateId,
    criterionId,
    citationIds: [],
    aiScore,
    aiStatus: status,
    aiStrengths: [],
    aiWeaknesses: [],
    aiJustification: '',
    aiEvaluatedAt: ago(3 * DAY),
  };
  if (options.userValidated) {
    base.userScore = options.userScore ?? aiScore;
    base.userStatus = status;
    base.userValidatedAt = ago(HOUR * 6);
    base.userComment = '';
  }
  return base;
};

const GENNEVILLIERS_EVALUATIONS: Evaluation[] = [
  // Candidat 1 — Bouygues : tout évalué + validé (status verified)
  makeEval('c-g-1', 'k-g-1.1', 17.5, 20, 'conforme',    { userValidated: true, userScore: 18 }),
  makeEval('c-g-1', 'k-g-1.2', 12.0, 15, 'partial',     { userValidated: true }),
  makeEval('c-g-1', 'k-g-1.3',  8.5, 10, 'conforme',    { userValidated: true }),
  makeEval('c-g-1', 'k-g-1.4', 13.0, 15, 'conforme',    { userValidated: true }),
  makeEval('c-g-1', 'k-g-2',   27.0, 30, 'conforme',    { userValidated: true }),
  makeEval('c-g-1', 'k-g-3.1',  2.5,  3, 'conforme',    { userValidated: true }),
  makeEval('c-g-1', 'k-g-3.2',  1.5,  2, 'partial',     { userValidated: true }),
  makeEval('c-g-1', 'k-g-4.1',  1.8,  2, 'conforme',    { userValidated: true }),
  makeEval('c-g-1', 'k-g-4.2',  2.5,  3, 'conforme',    { userValidated: true }),
  // Candidat 2 — Eiffage : tout évalué par IA, pas encore validé
  makeEval('c-g-2', 'k-g-1.1', 18.5, 20, 'conforme'),
  makeEval('c-g-2', 'k-g-1.2', 13.5, 15, 'conforme'),
  makeEval('c-g-2', 'k-g-1.3',  9.0, 10, 'conforme'),
  makeEval('c-g-2', 'k-g-1.4', 14.0, 15, 'conforme'),
  makeEval('c-g-2', 'k-g-2',   24.0, 30, 'partial'),
  makeEval('c-g-2', 'k-g-3.1',  2.0,  3, 'partial'),
  makeEval('c-g-2', 'k-g-3.2',  1.5,  2, 'partial'),
  makeEval('c-g-2', 'k-g-4.1',  2.0,  2, 'conforme'),
  makeEval('c-g-2', 'k-g-4.2',  2.8,  3, 'conforme'),
  // Candidat 3 — Engie : partiellement évalué (4 sur 9)
  makeEval('c-g-3', 'k-g-1.1', 14.0, 20, 'partial'),
  makeEval('c-g-3', 'k-g-1.2', 10.5, 15, 'partial'),
  makeEval('c-g-3', 'k-g-1.3',  6.0, 10, 'partial'),
  makeEval('c-g-3', 'k-g-1.4',  8.0, 15, 'non_conforme'),
  // Candidat 4 — Vinci : rien
];

// ─────────────────────────────────────────────────────────────────────
// Projet 2 — Construction groupe scolaire Sartrouville (en cours, juste commencé)
// ─────────────────────────────────────────────────────────────────────

const SARTROUVILLE_FILES: UploadedFile[] = [
  { id: 'f-s-rc-1', name: 'Règlement de consultation.pdf', sizeBytes: 3_100_000, pages: 24, uploadedAt: ago(3 * DAY), source: 'regulation' },
  { id: 'f-s-c1-1', name: 'Mémoire technique GCC Construction.pdf', sizeBytes: 22_400_000, pages: 198, uploadedAt: ago(1 * DAY), source: 'candidate', candidateId: 'c-s-1' },
  { id: 'f-s-c2-1', name: 'Mémoire technique Léon Grosse.pdf', sizeBytes: 17_900_000, pages: 142, uploadedAt: ago(1 * DAY), source: 'candidate', candidateId: 'c-s-2' },
];

const SARTROUVILLE_CANDIDATES: Candidate[] = [
  { id: 'c-s-1', name: 'GCC Construction', fileIds: ['f-s-c1-1'], totalPages: 198, status: 'todo' },
  { id: 'c-s-2', name: 'Léon Grosse', fileIds: ['f-s-c2-1'], totalPages: 142, status: 'todo' },
];

const SARTROUVILLE_CRITERIA: Criterion[] = [
  { id: 'k-s-1', parentId: null, code: '1', title: 'Valeur technique',       weight: 50, type: 'non_financial', scope: 'global', active: true, order: 0 },
  { id: 'k-s-2', parentId: null, code: '2', title: 'Prix',                   weight: 40, type: 'financial',     scope: 'global', active: true, order: 1 },
  { id: 'k-s-3', parentId: null, code: '3', title: 'Délais d\'exécution',    weight: 10, type: 'non_financial', scope: 'global', active: true, order: 2 },
];

// ─────────────────────────────────────────────────────────────────────
// Projet 3 — Maintenance chauffage Collège Jean-Moulin (complété)
// ─────────────────────────────────────────────────────────────────────

const JEAN_MOULIN_FILES: UploadedFile[] = [
  { id: 'f-jm-rc-1', name: 'Règlement de consultation.pdf', sizeBytes: 1_900_000, pages: 14, uploadedAt: ago(45 * DAY), source: 'regulation' },
  { id: 'f-jm-c1-1', name: 'Mémoire Dalkia.pdf', sizeBytes: 5_200_000, pages: 48, uploadedAt: ago(42 * DAY), source: 'candidate', candidateId: 'c-jm-1' },
  { id: 'f-jm-c2-1', name: 'Mémoire Idex Énergies.pdf', sizeBytes: 6_100_000, pages: 52, uploadedAt: ago(42 * DAY), source: 'candidate', candidateId: 'c-jm-2' },
  { id: 'f-jm-c3-1', name: 'Mémoire Engie Solutions.pdf', sizeBytes: 4_800_000, pages: 41, uploadedAt: ago(42 * DAY), source: 'candidate', candidateId: 'c-jm-3' },
];

const JEAN_MOULIN_CANDIDATES: Candidate[] = [
  { id: 'c-jm-1', name: 'Dalkia', fileIds: ['f-jm-c1-1'], totalPages: 48, status: 'verified' },
  { id: 'c-jm-2', name: 'Idex Énergies', fileIds: ['f-jm-c2-1'], totalPages: 52, status: 'verified' },
  { id: 'c-jm-3', name: 'Engie Solutions', fileIds: ['f-jm-c3-1'], totalPages: 41, status: 'verified' },
];

const JEAN_MOULIN_CRITERIA: Criterion[] = [
  { id: 'k-jm-1', parentId: null, code: '1', title: 'Valeur technique',         weight: 50, type: 'non_financial', scope: 'global', active: true, order: 0 },
  { id: 'k-jm-2', parentId: null, code: '2', title: 'Prix',                     weight: 30, type: 'financial',     scope: 'global', active: true, order: 1 },
  { id: 'k-jm-3', parentId: null, code: '3', title: 'Délais d\'intervention',   weight: 10, type: 'non_financial', scope: 'global', active: true, order: 2 },
  { id: 'k-jm-4', parentId: null, code: '4', title: 'Reporting et suivi',       weight: 10, type: 'non_financial', scope: 'global', active: true, order: 3 },
];

const JEAN_MOULIN_EVALUATIONS: Evaluation[] = JEAN_MOULIN_CANDIDATES.flatMap(cand =>
  JEAN_MOULIN_CRITERIA.map(crit => makeEval(cand.id, crit.id, crit.weight * 0.85, crit.weight, 'conforme', { userValidated: true }))
);

// ─────────────────────────────────────────────────────────────────────
// Projet 4 — Réfection toiture EHPAD Les Mimosas (démarrage, pas de RC)
// ─────────────────────────────────────────────────────────────────────

const EHPAD_FILES: UploadedFile[] = [];
const EHPAD_CANDIDATES: Candidate[] = [];
const EHPAD_CRITERIA: Criterion[] = [];

// ─────────────────────────────────────────────────────────────────────
// Enrichissement : points forts / faibles / justification + citations
// pour quelques évaluations, afin d'alimenter le drawer avec du vrai contenu.
// ─────────────────────────────────────────────────────────────────────

const enrichEval = (
  candidateId: string,
  criterionId: string,
  data: {
    strengths: string[];
    weaknesses: string[];
    justification: string;
    citations?: { text: string; sourceFileId: string; sourcePage: number; verified: boolean }[];
  }
) => {
  const evalIndex = GENNEVILLIERS_EVALUATIONS.findIndex(
    e => e.candidateId === candidateId && e.criterionId === criterionId
  );
  if (evalIndex === -1) return;
  const target = GENNEVILLIERS_EVALUATIONS[evalIndex];
  target.aiStrengths = data.strengths;
  target.aiWeaknesses = data.weaknesses;
  target.aiJustification = data.justification;
  const citations: Citation[] = (data.citations ?? []).map((c, i) => ({
    id: `cit-${target.id}-${i}`,
    evaluationId: target.id,
    text: c.text,
    sourceFileId: c.sourceFileId,
    sourcePage: c.sourcePage,
    verified: c.verified,
    origin: 'ai',
  }));
  target.citationIds = citations.map(c => c.id);
  ALL_CITATIONS.push(...citations);
};

const ALL_CITATIONS: Citation[] = [];

// Bouygues × 1.1 Performance énergétique — cellule vitrine
enrichEval('c-g-1', 'k-g-1.1', {
  strengths: [
    'Solution ITE en laine de bois biosourcée, certifiée ACERMI.',
    'Calculs thermiques détaillés conformes RE2020, avec simulation dynamique.',
    'Référence comparable (Mairie de Bagneux) citée avec retour d\'expérience chiffré.',
  ],
  weaknesses: [
    'Pas de précision sur la gestion des ponts thermiques en pignon.',
  ],
  justification:
    'L\'offre technique est solide sur le cœur thermique : matériaux performants, études détaillées, références pertinentes. L\'absence de précision sur les ponts thermiques en pignon empêche d\'atteindre la note maximale mais reste un point mineur.',
  citations: [
    {
      text: 'La solution retenue consiste en une ITE de 180 mm en laine de bois densité 55 kg/m³, certifiée ACERMI sous le numéro 12/345/6789, garantissant un λ de 0,038 W/(m·K).',
      sourceFileId: 'f-g-c1-1',
      sourcePage: 42,
      verified: true,
    },
    {
      text: 'Nos études thermiques dynamiques (logiciel Pleiades) confirment un gain énergétique de 58 % sur les consommations de chauffage par rapport à la situation actuelle.',
      sourceFileId: 'f-g-c1-1',
      sourcePage: 47,
      verified: true,
    },
    {
      text: 'Référence : rénovation de la Mairie de Bagneux (2023), où la même solution a permis une réduction mesurée de 61 % après 18 mois d\'exploitation.',
      sourceFileId: 'f-g-c1-1',
      sourcePage: 52,
      verified: false,
    },
  ],
});

// Bouygues × 1.2 Méthodologie d'exécution
enrichEval('c-g-1', 'k-g-1.2', {
  strengths: [
    'Planning phasé proposé avec tranches fermes / optionnelles.',
    'Équipe dédiée identifiée (chef de chantier + conducteur de travaux).',
  ],
  weaknesses: [
    'Méthodologie générique sur la coordination avec les occupants.',
    'Pas de plan de prévention détaillé pour les phases chantier occupé.',
  ],
  justification:
    'Méthodologie correcte mais manque de spécificité sur la coexistence chantier / bâtiment occupé, enjeu majeur d\'un bâtiment municipal en activité.',
  citations: [
    {
      text: 'Le chantier sera organisé en 3 tranches successives, chacune permettant de maintenir l\'activité dans l\'aile opposée du bâtiment.',
      sourceFileId: 'f-g-c1-1',
      sourcePage: 58,
      verified: true,
    },
  ],
});

// Bouygues × 2 Prix (pas de citations – c'est un critère financier)
enrichEval('c-g-1', 'k-g-2', {
  strengths: [
    'Prix compétitif (offre à 2 145 000 € HT, dans la fourchette basse des offres).',
    'Décomposition détaillée par lot technique.',
  ],
  weaknesses: [],
  justification:
    'Offre financière très compétitive, bien décomposée. Score élevé obtenu via la formule du règlement (moins-disant × 30 / offre évaluée).',
  citations: [],
});

// Eiffage × 1.1 — pour montrer une 2ᵉ cellule riche côté drawer
enrichEval('c-g-2', 'k-g-1.1', {
  strengths: [
    'Solution hybride ITE + ITI proposée pour les façades patrimoniales.',
    'Étude ACV complète sur le cycle de vie des matériaux (50 ans).',
    'Traitement exhaustif des ponts thermiques.',
    'Variante géothermie proposée en option.',
  ],
  weaknesses: [
    'Solution ITI sur 30% des façades pose question sur la conservation de surface utile intérieure.',
  ],
  justification:
    'Offre technique très complète avec traitement exemplaire des spécificités patrimoniales. Le recours partiel à l\'ITI génère un compromis sur les surfaces intérieures mais reste défendable.',
  citations: [
    {
      text: 'Pour préserver les modénatures de la façade principale classée, nous proposons une ITI performante (laine minérale 120 mm) sur ces 30 % de surface, complétée par une ITE standard ailleurs.',
      sourceFileId: 'f-g-c2-1',
      sourcePage: 33,
      verified: false,
    },
    {
      text: 'L\'ACV menée sur 50 ans démontre une empreinte carbone inférieure de 22 % aux solutions conventionnelles, principalement due au choix de la laine de bois et du béton bas carbone.',
      sourceFileId: 'f-g-c2-2',
      sourcePage: 8,
      verified: false,
    },
  ],
});

export const MOCK_CITATIONS: Citation[] = ALL_CITATIONS;

export const MOCK_PROJECTS: Project[] = [
  {
    id: 'p-gennevilliers',
    name: 'Rénovation énergétique Mairie de Gennevilliers',
    reference: 'AO-2026-042',
    status: 'in_progress',
    createdAt: ago(14 * DAY),
    updatedAt: ago(2 * HOUR),
    files: GENNEVILLIERS_FILES,
    candidates: GENNEVILLIERS_CANDIDATES,
    criteria: GENNEVILLIERS_CRITERIA,
    evaluations: GENNEVILLIERS_EVALUATIONS,
    citations: MOCK_CITATIONS,
    criteriaValidated: true,
  },
  {
    id: 'p-sartrouville',
    name: 'Construction groupe scolaire Sartrouville',
    reference: 'AO-2026-038',
    status: 'in_progress',
    createdAt: ago(5 * DAY),
    updatedAt: ago(26 * HOUR),
    files: SARTROUVILLE_FILES,
    candidates: SARTROUVILLE_CANDIDATES,
    criteria: SARTROUVILLE_CRITERIA,
    evaluations: [],
    citations: [],
    // Critères extraits mais pas encore validés : cas de démo du bandeau extraction.
    criteriaValidated: false,
  },
  {
    id: 'p-jean-moulin',
    name: 'Maintenance chauffage Collège Jean-Moulin',
    reference: 'AO-2025-127',
    status: 'completed',
    createdAt: ago(48 * DAY),
    updatedAt: ago(20 * DAY),
    files: JEAN_MOULIN_FILES,
    candidates: JEAN_MOULIN_CANDIDATES,
    criteria: JEAN_MOULIN_CRITERIA,
    evaluations: JEAN_MOULIN_EVALUATIONS,
    citations: [],
    criteriaValidated: true,
  },
  {
    id: 'p-ehpad-mimosas',
    name: 'Réfection toiture EHPAD Les Mimosas',
    status: 'in_progress',
    createdAt: ago(8 * HOUR),
    updatedAt: ago(8 * HOUR),
    files: EHPAD_FILES,
    candidates: EHPAD_CANDIDATES,
    criteria: EHPAD_CRITERIA,
    evaluations: [],
    citations: [],
    criteriaValidated: false,
  },
];

// Référence partagée pour les helpers de stats (utilise la même date "now").
export const REFERENCE_NOW_ISO = new Date(now).toISOString();

// Liste des IDs feuilles actifs pour Gennevilliers — utile pour l'étape Évaluations.
export const GENNEVILLIERS_ACTIVE_LEAF_CRITERIA = GENNEVILLIERS_LEAF_ACTIVE_IDS;
